import { createTheme } from '@mui/material/styles';

// ── CallEssential Edge — brand palette ───────────────────────────────────────
// Aligned with callessential.cc: clean corporate SaaS, primary blue + sky accent,
// soft blue-grey surfaces, generous radii and subtle depth.
const BRAND = {
  blue: '#2563EB',      // primary
  blueDark: '#1E40AF',
  blueLight: '#60A5FA',
  sky: '#0EA5E9',       // secondary / accent
  ink: '#0F172A',       // headings / primary text
  slate: '#64748B',     // secondary text
  line: '#E6EBF3',      // hairline borders
  surface: '#F5F7FB',   // app background
};

const shared = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600 },
  },
  shape: { borderRadius: 10 },
};

export const lightTheme = createTheme({
  ...shared,
  palette: {
    mode: 'light',
    primary: { main: BRAND.blue, dark: BRAND.blueDark, light: BRAND.blueLight, contrastText: '#ffffff' },
    secondary: { main: BRAND.sky, dark: '#0284C7', light: '#38BDF8', contrastText: '#ffffff' },
    success: { main: '#16A34A' },
    warning: { main: '#F59E0B' },
    error: { main: '#DC2626' },
    info: { main: BRAND.sky },
    background: { default: BRAND.surface, paper: '#ffffff' },
    text: { primary: BRAND.ink, secondary: BRAND.slate },
    divider: BRAND.line,
  },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { backgroundColor: BRAND.surface } } },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${BRAND.line}`,
          borderRadius: 14,
          boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)',
        },
      },
    },
    MuiPaper: { styleOverrides: { outlined: { borderColor: BRAND.line } } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 9, paddingInline: 16 },
        containedPrimary: { boxShadow: '0 1px 2px rgba(37,99,235,0.35)', '&:hover': { boxShadow: '0 3px 8px rgba(37,99,235,0.30)' } },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
    MuiTableHead: {
      styleOverrides: { root: { '& th': { fontWeight: 700, backgroundColor: '#F1F5FB', color: BRAND.ink, borderBottom: `1px solid ${BRAND.line}` } } },
    },
    MuiTableCell: { styleOverrides: { root: { borderBottomColor: BRAND.line } } },
    MuiAppBar: { styleOverrides: { root: { backdropFilter: 'saturate(180%) blur(6px)' } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: BRAND.ink, fontSize: 12 } } },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: 10 } } },
  },
});

export const darkTheme = createTheme({
  ...shared,
  palette: {
    mode: 'dark',
    primary: { main: BRAND.blueLight, dark: BRAND.blue, light: '#93C5FD', contrastText: '#08111f' },
    secondary: { main: '#38BDF8' },
    success: { main: '#22C55E' },
    warning: { main: '#FBBF24' },
    error: { main: '#F87171' },
    info: { main: '#38BDF8' },
    background: { default: '#0B1220', paper: '#111A2E' },
    text: { primary: '#E5EDF9', secondary: '#94A3B8' },
    divider: 'rgba(148,163,184,0.16)',
  },
  components: {
    MuiCard: { styleOverrides: { root: { border: '1px solid rgba(148,163,184,0.16)', borderRadius: 14, backgroundImage: 'none' } } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: 9, paddingInline: 16 } },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
    MuiTableHead: { styleOverrides: { root: { '& th': { fontWeight: 700, backgroundColor: '#16213A' } } } },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: 10 } } },
  },
});
